import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useCollection } from "@/hooks/firestore";
import { 
  useAddDocument, 
  useUpdateDocument, 
  useDeleteDocument 
} from "@/hooks/firestore/operations";
import { Permission, User } from "@/types/firestore";
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectUser, selectHasPermission } from '@/store/slices/authSlice';
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollableTable, Column, SortConfig } from "@/components/ui/scrollable-table";
import { ListFilter } from "lucide-react";

// Redux imports for deletion dialog
import { DeleteDialog } from "../editor/dialogs/DeleteConfirmDialog";
import { 
    EditableEntityType,
    startDeletionAssessment, 
    assessDeletionImpact, 
    deleteEntity as deleteUserEntity, // Renamed to avoid conflict with local deleteUser
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError
} from "@/store/slices/editorSlice";

export const UserManager = () => {
  const dispatch = useAppDispatch();
  
  // Get users collection
  const { documents: users, loading: usersLoading, error: usersError } = useCollection<User>('users');
  
  // Get permissions collection
  const { documents: permissionsData, loading: permissionsLoading, error: permissionsError } = useCollection<Permission>('permissions');
  
  // Firestore operations
  const { addDocument: addPermission, loading: addingPermission } = useAddDocument<Permission>('permissions');
  const { updateDocument: updateUser } = useUpdateDocument<User>('users');
  const { deleteDocument: deletePermission } = useDeleteDocument('permissions');
  
  // Editor slice selectors
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);

  // Get auth data from Redux
  const hasManageUsers = useAppSelector(selectHasPermission('manage_users'));
  const hasAdmin = useAppSelector(selectHasPermission('admin'));
  const currentUser = useAppSelector(selectUser);
  
  // State hooks
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [newPermission, setNewPermission] = useState({ identifier: '', description: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof User | 'permissionsCount' | null>('username');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedPermissionsFilter, setSelectedPermissionsFilter] = useState<string[]>([]);

  // Find current user's data to check admin status
  const currentUserData = users?.find(u => u.id === currentUser?.id);
  const currentUserIsAdmin = currentUserData?.isAdmin ?? false;
  
  // Check if user can manage users
  const canManage = hasManageUsers || hasAdmin;

  const processedUsers = useMemo(() => {
    if (!users) return [];
    let filteredUsers = users;

    // Filter by search term (username or email)
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filteredUsers = filteredUsers.filter(user =>
        user.username.toLowerCase().includes(lowerSearchTerm) ||
        (user.email && user.email.toLowerCase().includes(lowerSearchTerm))
      );
    }

    // Filter by selected permissions (user must have ALL selected permissions)
    if (selectedPermissionsFilter.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        selectedPermissionsFilter.every(filterPerm => 
          user.permissions?.includes(filterPerm)
        )
      );
    }

    // Sort
    if (sortColumn) {
      filteredUsers = [...filteredUsers].sort((a, b) => {
        let aValue: any, bValue: any;
        if (sortColumn === 'permissionsCount') {
            aValue = a.permissions?.length || 0;
            bValue = b.permissions?.length || 0;
        } else {
            aValue = a[sortColumn as keyof User]; // Type assertion
            bValue = b[sortColumn as keyof User]; // Type assertion
        }

        if (aValue === undefined || aValue === null) aValue = '' // Handle undefined/null for robust sorting
        if (bValue === undefined || bValue === null) bValue = ''

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        return 0;
      });
    }
    return filteredUsers;
  }, [users, searchTerm, sortColumn, sortDirection, selectedPermissionsFilter]);

  const handleSort = (column: keyof User | 'permissionsCount') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  const handlePermissionFilterChange = (permissionId: string) => {
    setSelectedPermissionsFilter(prev => 
      prev.includes(permissionId) 
        ? prev.filter(p => p !== permissionId) 
        : [...prev, permissionId]
    );
  };

  const handleAddPermission = useCallback(async (): Promise<void> => {
    if (!newPermission.identifier.trim() || !newPermission.description.trim()) {
      toast.error("Both identifier and description are required");
      return;
    }

    try {
      const newId = await addPermission({
        identifier: newPermission.identifier.trim(),
        description: newPermission.description.trim()
      });
      
      if (newId) {
        setNewPermission({ identifier: '', description: '' });
        setIsPermissionModalOpen(false);
        toast.success('Permission added successfully');
      } else {
        toast.error('Failed to add permission');
      }
    } catch (error) {
      console.error("Error adding permission:", error);
      toast.error('Failed to add permission');
    }
  }, [addPermission, newPermission]);

  const handleUpdateUserPermissions = useCallback(async (userId: string, permissions: string[]): Promise<void> => {
    try {
      const success = await updateUser(userId, { permissions });
      if (success) {
        toast.success('User permissions updated');
      } else {
        toast.error('Failed to update user permissions');
      }
    } catch (error) {
      console.error("Error updating user permissions:", error);
      toast.error('Failed to update user permissions');
    }
  }, [updateUser]);

  const handleDeletePermission = useCallback(async (permissionId: string): Promise<void> => {
    if (!permissionId) {
      toast.error("Cannot delete permission: Missing ID");
      return;
    }
    
    if (confirm("Are you sure you want to delete this permission? This cannot be undone.")) {
      try {
        const success = await deletePermission(permissionId);
        if (success) {
          toast.success("Permission deleted successfully");
        } else {
          toast.error("Failed to delete permission");
        }
      } catch (error) {
        console.error("Error deleting permission:", error);
        toast.error("Failed to delete permission");
      }
    }
  }, [deletePermission]);

  // Deletion Logic using Redux Dialog
  const handleDeleteUserAction = useCallback((user: User) => {
    if (!user || !user.id) return;
    if (user.id === currentUser?.id) {
      toast.error("You cannot delete yourself.");
      return;
    }
    if (user.isAdmin && !currentUserIsAdmin) {
        toast.error("You do not have permission to delete an admin user.");
        return;
    }
    dispatch(startDeletionAssessment({ entityType: 'user', entityId: user.id }));
    // Users might not have a complex impact assessment, but we dispatch for consistency.
    // The editorSlice's startDeletionAssessment will handle 'user' type appropriately (e.g., skip if configured).
    // If 'user' type requires specific impact logic, assessDeletionImpact can be called here if needed.
    // For now, assuming startDeletionAssessment handles it or simple confirm is enough.
    dispatch(assessDeletionImpact({ entityType: 'user', entityId: user.id }));
  }, [dispatch, currentUser, currentUserIsAdmin]);

  const handleDeletionDialogClose = useCallback(() => {
    dispatch(cancelInteraction());
  }, [dispatch]);

  const handleConfirmUserDeletion = useCallback(async () => {
    if (selectedEntity && selectedEntity.type === 'user' && selectedEntity.id) {
      try {
        await dispatch(deleteUserEntity({ entityType: 'user', entityId: selectedEntity.id })).unwrap();
        
        // cancelInteraction is called by deleteEntity.fulfilled
      } catch (error: any) {
        // Error handling is now done by the dialog component
        throw error;
      }
    }
  }, [dispatch, selectedEntity]);

  const getDeletionAssessmentState = useCallback(() => {
    if (deletionImpact.error) return 'error';
    if (deletionImpact.loading === 'pending') return 'loading';
    return 'completed';
  }, [deletionImpact.loading, deletionImpact.error]);

  // Create table columns configuration - MUST come before early returns
  const tableColumns = useMemo(() => {
    const columns: Column<User>[] = [];
    
    columns.push({
      id: 'username',
      header: 'Username',
      cell: (user) => (
        <div className="flex items-center gap-2">
          {user.username}
          {user.isAdmin && <Badge className="bg-amber-500 text-white">Admin</Badge>}
        </div>
      ),
      sortable: true,
      minWidth: '150px'
    });
    
    columns.push({
      id: 'email',
      header: 'Email',
      cell: (user) => user.email || 'N/A',
      sortable: true,
      minWidth: '200px'
    });
    
    columns.push({
      id: 'permissions',
      header: 'Permissions',
      cell: (user) => (
        <div className="flex gap-1 flex-wrap max-w-xs">
          {user.permissions?.map((permissionId: string) => (
            <Badge key={permissionId} variant="secondary" className="text-xs">
              {permissionsData?.find((p: Permission) => p.identifier === permissionId)?.identifier || permissionId}
            </Badge>
          ))}
          {(!user.permissions || user.permissions.length === 0) && (
             <span className="text-xs text-muted-foreground">No permissions</span>
          )}
        </div>
      ),
      sortable: true,
      minWidth: '250px'
    });
    
    if (canManage) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        cell: (user) => {
          // Determine if the button should be disabled for this specific user row
          const isTargetUserAdmin = user.isAdmin ?? false;
          const disableEditButton = !canManage || (isTargetUserAdmin && !currentUserIsAdmin);
          const disableDeleteButton = disableEditButton || user.id === currentUser?.id; // Cannot delete self
          
          return (
            <div className="flex justify-end gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={disableEditButton}>
                    Edit Permissions
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit User Permissions</DialogTitle>
                    <DialogDescription>
                      Select the permissions for {user.username}
                      {user.isAdmin && <span className="ml-2 text-amber-500 font-semibold">(Admin)</span>}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                    {permissionsData?.map((permission: Permission) => (
                      <div key={permission.id} className="flex items-start space-x-3 p-2 border rounded-md hover:bg-muted/50">
                        <Checkbox
                          id={`${user.id}-${permission.id}`}
                          checked={user.permissions?.includes(permission.identifier)}
                          onCheckedChange={(checked) => {
                            if (
                              !checked && 
                              permission.identifier === 'manage_users' && 
                              user.id === currentUser?.id
                            ) {
                              toast.error("You cannot remove your own 'manage_users' permission.");
                              return;
                            }
                            
                            const currentPermissions = user.permissions || [];
                            const newPermissions = checked
                              ? [...currentPermissions, permission.identifier]
                              : currentPermissions.filter((p: string) => p !== permission.identifier);
                              
                            const uniquePermissions: string[] = Array.from(new Set(newPermissions));  
                            user.id && handleUpdateUserPermissions(user.id, uniquePermissions);
                          }}
                          disabled={disableEditButton}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label
                            htmlFor={`${user.id}-${permission.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 block cursor-pointer"
                          >
                            {permission.identifier}
                          </label>
                          {permission.description && (
                            <span className="block text-xs text-muted-foreground mt-1">
                              {permission.description}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => handleDeleteUserAction(user)}
                disabled={disableDeleteButton}
              >
                Delete User
              </Button>
            </div>
          );
        },
        sortable: false,
        minWidth: '200px'
      });
    }
    
    return columns;
  }, [permissionsData, canManage, currentUserIsAdmin, currentUser?.id, handleUpdateUserPermissions, handleDeleteUserAction]);
  
  // Create sort config for the table
  const tableSortConfig: SortConfig<User> = useMemo(() => {
    // Handle special case for permissionsCount which isn't a direct keyof User
    if (sortColumn === 'permissionsCount') {
      return {
        column: null, // We'll handle this sorting in the processedUsers useMemo
        direction: sortDirection
      };
    }
    return {
      column: sortColumn as keyof User | null,
      direction: sortDirection
    };
  }, [sortColumn, sortDirection]);
  
  // Empty message with filters applied
  const emptyMessage = useMemo(() => {
    let message = "No users found matching your criteria";
    if (searchTerm) message += ` for "${searchTerm}"`;
    if (selectedPermissionsFilter.length > 0) {
      message += ` with permissions: ${selectedPermissionsFilter.join(', ')}`;
    }
    return message + '.';
  }, [searchTerm, selectedPermissionsFilter]);

  // Add loading and error states
  if (usersLoading || permissionsLoading) {
    return <div className="flex justify-center items-center h-full">Loading users and permissions...</div>;
  }

  if (usersError || permissionsError) {
    return <div className="flex justify-center items-center h-full text-destructive">Error loading data. Please try again later.</div>;
  }

  return (
    <>
      <div className="flex flex-col gap-6 p-4 md:p-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div>
                <CardTitle>User Management ({processedUsers?.length || 0} / {users?.length || 0})</CardTitle>
                <CardDescription>Manage users and their permissions</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch md:items-center">
                  <Input 
                      placeholder="Search username or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="max-w-xs"
                  />
                  <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full sm:w-auto">
                              <ListFilter className="h-4 w-4 mr-2" /> Permissions ({selectedPermissionsFilter.length > 0 ? selectedPermissionsFilter.length : 'All'})
                          </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[250px]">
                          <DropdownMenuLabel>Filter by Permissions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {permissionsData?.map((permission) => (
                              <DropdownMenuCheckboxItem
                                  key={permission.id}
                                  checked={selectedPermissionsFilter.includes(permission.identifier)}
                                  onCheckedChange={() => handlePermissionFilterChange(permission.identifier)}
                              >
                                  {permission.identifier}
                              </DropdownMenuCheckboxItem>
                          ))}
                           {selectedPermissionsFilter.length > 0 && (
                              <>
                                  <DropdownMenuSeparator />
                                  <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="w-full justify-start text-sm"
                                      onClick={() => setSelectedPermissionsFilter([])}
                                  >
                                      Clear Filters
                                  </Button>
                              </>
                          )}
                      </DropdownMenuContent>
                  </DropdownMenu>
                  {canManage && (
                    <Dialog open={isPermissionModalOpen} onOpenChange={setIsPermissionModalOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full sm:w-auto">Manage Permissions</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Manage Permissions</DialogTitle>
                          <DialogDescription>
                            Add or remove permissions that can be assigned to users.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="permIdentifier">Identifier</Label>
                            <Input
                              id="permIdentifier"
                              placeholder="e.g., manage_settings"
                              value={newPermission.identifier}
                              onChange={(e) => setNewPermission(prev => ({ ...prev, identifier: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                              disabled={!canManage}
                            />
                            <Label htmlFor="permDescription">Description</Label>
                            <Input
                              id="permDescription"
                              placeholder="e.g., Allows managing system settings"
                              value={newPermission.description}
                              onChange={(e) => setNewPermission(prev => ({ ...prev, description: e.target.value }))}
                              disabled={!canManage}
                            />
                            <Button onClick={handleAddPermission} className="w-full" disabled={!canManage || !newPermission.identifier.trim() || !newPermission.description.trim() || addingPermission}>
                              Add Permission
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <h4 className="font-medium text-sm">Existing Permissions ({permissionsData?.length || 0})</h4>
                            <div className="max-h-[200px] overflow-y-auto pr-2 space-y-1">
                              {permissionsData?.map((permission: Permission) => (
                                <div key={permission.id} className="flex justify-between items-center p-2 border rounded-md">
                                  <div>
                                    <p className="font-medium text-sm">{permission.identifier}</p>
                                    <p className="text-xs text-muted-foreground">{permission.description}</p>
                                  </div>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => permission.id && handleDeletePermission(permission.id)}
                                    disabled={!canManage}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollableTable
              data={processedUsers}
              columns={tableColumns}
              sortConfig={tableSortConfig}
              onSort={handleSort}
              emptyMessage={emptyMessage}
              height="calc(100vh - 320px)"
            />
          </CardContent>
        </Card>
      </div>

      {/* Deletion Dialog */}
      {(interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && selectedEntity?.type === 'user' && (
        <DeleteDialog
          isOpen={true}
          onClose={handleDeletionDialogClose}
          entityType={selectedEntity.type as EditableEntityType}
          entityName={deletionImpact.report?.primaryDocument.name || selectedEntity.id || 'User'}
          assessmentState={getDeletionAssessmentState()}
          impactReport={deletionImpact.report}
          deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
          error={deletionImpact.error || operationError}
          onConfirm={handleConfirmUserDeletion}
        />
      )}
    </>
  );
};

export default UserManager; 